/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useLoggingSettingsData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading logging settings data.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { getCurrentLoggingSettings } from '../api/loggingSettingsApi';
import type { LoggingSettings } from '../types';
import {
  DEFAULT_CONSOLE_SETTINGS,
  DEFAULT_FILE_LOGGING
} from '../types';

/**
 * Custom React hook for loading global Logging Settings data.
 * Retrieves Console and File logging configurations from the API, 
 * applying backward-compatible transformations for legacy settings.
 * 
 * @returns Object containing the loading state, form data, and the state setter.
 */
export function useLoggingSettingsData() {
  const { user } = useAuthStore();
  const { showError } = useNotifications();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<LoggingSettings>({
    domainId: 0,
    consoleSettings: DEFAULT_CONSOLE_SETTINGS,
    fileLogging: DEFAULT_FILE_LOGGING,
    enabled: true
  });
  const configLoadedRef = useRef(false);

  useEffect(() => {
    const loadConfig = async () => {
      if (configLoadedRef.current || !user?.tenant?.slug || !user?.id) return;
      configLoadedRef.current = true;

      try {
        setLoading(true);
        const result = await getCurrentLoggingSettings(user.tenant.slug, Number(user.id));

        if (result.success && result.data) {
          // Handle backward compatibility: if loglevel is a string, convert to array
          const consoleSettings = result.data.consoleSettings || DEFAULT_CONSOLE_SETTINGS;
          if (consoleSettings.loglevel && typeof consoleSettings.loglevel === 'string') {
            consoleSettings.loglevel = [consoleSettings.loglevel];
          } else if (!Array.isArray(consoleSettings.loglevel)) {
            consoleSettings.loglevel = DEFAULT_CONSOLE_SETTINGS.loglevel;
          }

          const fileLogging = result.data.fileLogging || DEFAULT_FILE_LOGGING;
          // Ensure we always have at least one profile with one mapping
          if (!fileLogging.profiles || fileLogging.profiles.length === 0) {
            fileLogging.profiles = [DEFAULT_FILE_LOGGING.profiles[0]];
          } else {
            // Ensure first profile has at least one mapping
            if (!fileLogging.profiles[0].mappings || fileLogging.profiles[0].mappings.length === 0) {
              fileLogging.profiles[0].mappings = [DEFAULT_FILE_LOGGING.profiles[0].mappings[0]];
            }
          }

          setFormData({
            domainId: result.data.domainId,
            consoleSettings,
            fileLogging,
            consoleXml: result.data.consoleXml,
            fileXml: result.data.fileXml,
            enabled: result.data.enabled ?? true
          });
        }
      } catch (error) {
        console.error('Failed to load logging settings:', error);
        showError(t('loggingSettings.messages.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [user?.tenant?.slug, user?.id, t, showError]);

  return {
    loading,
    formData,
    setFormData
  };
}

